import { useMemo } from 'react';
import {
  isOccasionSuggestionHandled,
  liveObligationTitle,
  occasionOccurrenceKey,
  occasionReminderDraft,
  occasionSuggestionDue,
  useCreateObligation,
  useDeleteObligation,
  useNowMinute,
  useObligations,
  useUpdateObligation,
} from '../lib/obligations.js';
import { usePeople } from '../lib/people.js';
import { useUndoToast } from '../lib/undoToast.js';

/**
 * Proactive occasion → task prompt, shown on Today and at the top of
 * the Obligations list — the user shouldn't have to open a birthday to
 * be offered a gift reminder. A second reader of the same suggestion
 * state as the preview-card strip (`suggestionDismissedFor`), so
 * adding or dismissing from either surface quiets both.
 *
 * Shows the nearest unhandled occasion in the window with one-tap
 * actions; further ones are a "+N more" hint (they surface here as
 * each is handled, or via their own cards). Tapping the text opens
 * the occasion's preview via `onOpen`.
 */
export function OccasionSuggestionBanner({ onOpen }: { onOpen: (id: string) => void }) {
  const obligations = useObligations();
  const people = usePeople();
  const update = useUpdateObligation();
  const create = useCreateObligation();
  const del = useDeleteObligation();
  const undo = useUndoToast();
  const now = useNowMinute();

  const pending = useMemo(
    () =>
      (obligations.data ?? [])
        .filter(
          (o) => occasionSuggestionDue(o, new Date(now)) && !isOccasionSuggestionHandled(o),
        )
        .sort((a, b) => occasionOccurrenceKey(a).localeCompare(occasionOccurrenceKey(b))),
    [obligations.data, now],
  );

  if (pending.length === 0) return null;
  const occasion = pending[0]!;
  const title = liveObligationTitle(occasion, people.data);
  const isBirthday = occasion.occasionType !== 'anniversary';
  const more = pending.length - 1;

  const key = occasionOccurrenceKey(occasion);
  const target = new Date(`${key}T00:00:00`);
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  const days = Math.round((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  const rel =
    days === 0 ? 'today' : days === 1 ? 'tomorrow' : days < 14 ? `in ${days} days` : `in ${Math.round(days / 7)} weeks`;

  function dismiss() {
    update.mutate({ id: occasion.id, input: { suggestionDismissedFor: key } });
    undo.show('Suggestion dismissed', () => {
      update.mutate({ id: occasion.id, input: { suggestionDismissedFor: null } });
    });
  }

  function addReminder() {
    const draft = occasionReminderDraft(occasion, title);
    create.mutate(draft);
    update.mutate({ id: occasion.id, input: { suggestionDismissedFor: key } });
    undo.show(`Added “${draft.title}”`, () => {
      del.mutate({ id: draft.id });
      update.mutate({ id: occasion.id, input: { suggestionDismissedFor: null } });
    });
  }

  return (
    <div className="preview-expiry preview-expiry--banner">
      <button
        type="button"
        className="preview-expiry__body"
        onClick={() => onOpen(occasion.id)}
        aria-label={`Open ${title}`}
      >
        {title}
        {isBirthday ? '’s birthday is ' : '’s anniversary is '}
        {rel}.
        {more > 0 && <span className="preview-expiry__more"> +{more} more</span>}
      </button>
      <span className="preview-expiry__actions">
        <button type="button" className="btn btn--secondary btn--small" onClick={dismiss}>
          Dismiss
        </button>
        <button type="button" className="btn btn--primary btn--small" onClick={addReminder}>
          {isBirthday ? 'Add gift reminder' : 'Add a reminder'}
        </button>
      </span>
    </div>
  );
}
